/**
 * Release Student — supports two scopes:
 *   • "This class only"       → POST /class-schedule/{occurrenceId}/remove-student
 *                               (single-occurrence excused-removal; recurring
 *                                class and future sessions untouched)
 *   • "This and future"       → POST /class-schedules/{scheduleId}/release-student
 *                               (pattern-level release from an effective date)
 *
 * Past attendance and invoiced sessions are preserved by both endpoints.
 * The "This class only" scope is only offered when the caller supplies an
 * explicit occurrenceId — the schedule-tab entry point (which has no single
 * session in context) still gets the original pattern-level flow.
 */
import React, { useMemo, useState } from "react";
import {
  Autocomplete, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, FormControlLabel, IconButton, MenuItem, Radio, RadioGroup,
  Stack, TextField, Typography, alpha, useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs from "dayjs";
import { useNotifier } from "../../../../../Core/Notifier";
import {
  useReleaseClassScheduleStudentMutation,
  useRemoveStudentFromOccurrenceMutation,
} from "../../../../../Api/attoDeskApi";
import AppDatePicker from "../../../../../Components/DatePicker/AppDatePicker";
import type { SchedulePattern } from "../ClassSchedulesTab";

/**
 * Standard release reasons. Independent of MakeupWorkflow's absence reasons
 * (sick / vacation / etc.) — releasing a student from a class answers a
 * different question. "Other" forces a manual reason text; backend stores
 * both the typed reason and a lowercased reason_type where the API accepts
 * it (occurrence endpoint). The pattern endpoint only stores free-text, so
 * the standard label is sent as the reason there.
 */
const RELEASE_REASONS = [
  "Schedule conflict",
  "Student unavailable",
  "Parent request",
  "Program completed",
  "Changed class",
  "Moved to another class",
  "Location unavailable",
  "Staff recommendation",
  "Billing / financial",
  "Other",
];

interface Props {
  open: boolean;
  pattern: SchedulePattern;
  /** Occurrence currently in context (enables "This class only" scope). */
  occurrenceId?: number | null;
  /** ISO date (YYYY-MM-DD) for that occurrence; drives the summary date. */
  occurrenceDate?: string | null;
  /** Flag passed through from the detail page when >1 pattern exists. */
  hasMultiplePatterns?: boolean;
  onClose: () => void;
  onDone: () => void;
}

type Scope = "occurrence" | "future";

const fmtTime = (t?: string) => {
  if (!t) return "";
  const [h, m] = String(t).split(":").map(Number);
  const d = new Date(); d.setHours(h, m || 0, 0, 0);
  return new Intl.DateTimeFormat("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).format(d);
};

const initials = (n?: string | null) =>
  (n || "").split(" ").filter(Boolean).slice(0, 2).map((x) => x[0]?.toUpperCase()).join("") || "?";

const ReleaseStudentDialog: React.FC<Props> = ({
  open, pattern, occurrenceId, occurrenceDate, hasMultiplePatterns, onClose, onDone,
}) => {
  const theme = useTheme();
  const { showMessage, showErrorMessage } = useNotifier();

  const scopeAllowsOccurrence = !!occurrenceId;
  const [scope, setScope] = useState<Scope>(scopeAllowsOccurrence ? "future" : "future");

  const [studentId, setStudentId] = useState<number | null>(
    pattern.students.length === 1 ? pattern.students[0].id : null,
  );
  const [effectiveDate, setEffectiveDate] = useState<string>(
    occurrenceDate || dayjs().format("YYYY-MM-DD"),
  );
  const [reasonSel, setReasonSel] = useState<string>("");
  const [customReason, setCustomReason] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  const [releaseFuture, { isLoading: savingFuture }] = useReleaseClassScheduleStudentMutation();
  const [removeOccurrence, { isLoading: savingOcc }] = useRemoveStudentFromOccurrenceMutation();
  const saving = savingFuture || savingOcc;

  const isOther = reasonSel === "Other";
  const effectiveReason = isOther ? customReason.trim() : reasonSel;
  const reasonTypePayload = isOther ? "other" : reasonSel;

  // Validate the Effective Release Date against the series bounds. The
  // single-occurrence scope has no editable date, so bounds don't apply.
  const dateError: string | null = (() => {
    if (scope !== "future") return null;
    if (!effectiveDate) return "Pick an effective release date.";
    const d = dayjs(effectiveDate);
    if (!d.isValid()) return "Invalid date.";
    if (pattern.series_start_date && d.isBefore(dayjs(pattern.series_start_date), "day")) {
      return `Effective date cannot be before the class start (${dayjs(pattern.series_start_date).format("MMM D, YYYY")}).`;
    }
    if (pattern.series_end_date && d.isAfter(dayjs(pattern.series_end_date), "day")) {
      return `Effective date cannot be after the class end (${dayjs(pattern.series_end_date).format("MMM D, YYYY")}).`;
    }
    return null;
  })();

  const canSubmit =
    !!studentId &&
    !!reasonSel &&
    (!isOther || customReason.trim().length > 0) &&
    (scope === "occurrence" ? !!occurrenceId : !!effectiveDate && !dateError) &&
    !saving;

  const selectedStudent = useMemo(
    () => pattern.students.find((s) => s.id === studentId) || null,
    [pattern.students, studentId],
  );

  const handleSubmit = async () => {
    if (!canSubmit || !studentId) return;
    try {
      if (scope === "occurrence" && occurrenceId) {
        const res = await removeOccurrence({
          occurrenceId,
          student_id: studentId,
          reason_type: reasonTypePayload,
          reason: effectiveReason,
          notes: notes.trim() || undefined,
          makeup_required: false,
          schedule_later: false,
        }).unwrap();
        if ((res as any)?.status !== false) {
          showMessage(`Student removed from this session.`);
          onDone();
        } else {
          showErrorMessage((res as any)?.message || "Failed to remove student.");
        }
      } else {
        const res = await releaseFuture({
          scheduleId: pattern.schedule_id,
          student_id: studentId,
          effective_date: effectiveDate,
          reason: effectiveReason,
          notes: notes.trim() || undefined,
        }).unwrap();
        if ((res as any)?.status !== false) {
          const removed = (res as any)?.data?.future_occurrences_removed ?? 0;
          showMessage(`Student released — ${removed} future session${removed === 1 ? "" : "s"} removed.`);
          onDone();
        } else {
          showErrorMessage((res as any)?.message || "Failed to release student.");
        }
      }
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Failed to release student.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px" } }}
    >
      <DialogTitle sx={{ pr: 6, fontSize: 16, fontWeight: 700 }}>
        Release Student from Class
        <IconButton onClick={onClose} size="small" disabled={saving} sx={{ position: "absolute", right: 12, top: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <ClassSummaryCard pattern={pattern} occurrenceDate={occurrenceDate} />

        {hasMultiplePatterns && (
          <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 1, fontStyle: "italic" }}>
            This action applies to the primary schedule pattern.
          </Typography>
        )}

        <Stack spacing={2.25} sx={{ mt: 2.5 }}>
          <StudentField
            students={pattern.students}
            value={selectedStudent}
            onChange={(s) => setStudentId(s ? s.id : null)}
          />

          {/* Always-visible Class Dates row. Lives directly above Release
              Scope (not only inside the collapsible summary card) so the
              user never has to scroll to see the series bounds before
              choosing a scope or an effective date. */}
          <ClassDatesRow
            seriesStartDate={pattern.series_start_date ?? null}
            seriesEndDate={pattern.series_end_date ?? null}
          />

          <Box>
            <SectionLabel>Release scope</SectionLabel>
            <RadioGroup value={scope} onChange={(_, v) => setScope(v as Scope)} sx={{ mt: 0.5 }}>
              <ScopeOption
                value="occurrence"
                selected={scope === "occurrence"}
                disabled={!scopeAllowsOccurrence}
                title="This class only"
                description={
                  scopeAllowsOccurrence
                    ? "Remove the student only from this dated session. The recurring class and future sessions remain unchanged."
                    : "Not available — no specific session is in context."
                }
              />
              <ScopeOption
                value="future"
                selected={scope === "future"}
                title="This and future classes"
                description="Release the student from this class starting on the effective date. Past attendance and invoices are preserved."
              />
            </RadioGroup>
          </Box>

          {scope === "occurrence" ? (
            <Box
              sx={{
                px: 1.5, py: 1.25, borderRadius: "10px",
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                display: "flex", gap: 2.5, flexWrap: "wrap",
              }}
            >
              <MetaField label="Session date" value={occurrenceDate ? dayjs(occurrenceDate).format("dddd, MMM D, YYYY") : "—"} />
              <MetaField label="Time" value={`${fmtTime(pattern.start_time)} – ${fmtTime(pattern.end_time)}`} />
            </Box>
          ) : (
            <AppDatePicker
              label="Effective release date"
              value={effectiveDate}
              onChange={(v) => setEffectiveDate(v)}
              required
              clearable={false}
              minDate={pattern.series_start_date || undefined}
              maxDate={pattern.series_end_date || undefined}
              error={!!dateError}
              helperText={
                dateError
                  ? dateError
                  : pattern.series_end_date
                    ? `The student will be removed from this class and all future occurrences on or after this date, up to the class end date (${dayjs(pattern.series_end_date).format("MMM D, YYYY")}).`
                    : "The student will be removed from this class and all future occurrences on or after this date."
              }
            />
          )}

          <TextField
            select
            label="Reason"
            size="small"
            required
            value={reasonSel}
            onChange={(e) => setReasonSel(e.target.value)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (v) =>
                v ? (v as string) : <span style={{ color: "#94a3b8" }}>Select reason</span>,
            }}
          >
            {RELEASE_REASONS.map((r) => (
              <MenuItem key={r} value={r}>{r}</MenuItem>
            ))}
          </TextField>

          {isOther && (
            <TextField
              label="Custom reason"
              size="small"
              required
              value={customReason}
              onChange={(e) => setCustomReason(e.target.value)}
              inputProps={{ maxLength: 500 }}
              helperText={`${customReason.length}/500`}
            />
          )}

          <TextField
            label="Notes (optional)"
            size="small"
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            inputProps={{ maxLength: 2000 }}
            helperText="Extra internal explanation — separate from the reason."
          />

          <SafetyMessage scope={scope} />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{ textTransform: "none", boxShadow: "none", borderRadius: "8px", minWidth: 150 }}
        >
          {saving ? "Releasing…" : "Release Student"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Presentation sub-components
// ─────────────────────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>
    {children}
  </Typography>
);

const MetaField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ minWidth: 140 }}>
    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b", mt: 0.25 }}>{value}</Typography>
  </Box>
);

const ClassSummaryCard: React.FC<{ pattern: SchedulePattern; occurrenceDate?: string | null }> = ({ pattern, occurrenceDate }) => {
  const theme = useTheme();
  const dateLine = occurrenceDate
    ? dayjs(occurrenceDate).format("dddd, MMMM D, YYYY")
    : pattern.day || "—";
  const timeLine = `${fmtTime(pattern.start_time)} – ${fmtTime(pattern.end_time)}`;

  const grades = pattern.grade_subjects.map((g) => g.grade).filter(Boolean);
  const uniqueSubjects: { id: number; name: string }[] = [];
  const seen = new Set<string>();
  for (const g of pattern.grade_subjects) {
    for (const s of g.subjects) {
      const key = `${s.id}::${s.name}`;
      if (!seen.has(key)) { seen.add(key); uniqueSubjects.push(s); }
    }
  }
  const SUBJECT_CAP = 6;
  const shownSubjects = uniqueSubjects.slice(0, SUBJECT_CAP);
  const moreCount = Math.max(0, uniqueSubjects.length - SUBJECT_CAP);
  const [expanded, setExpanded] = useState(false);
  const subjectsToRender = expanded ? uniqueSubjects : shownSubjects;

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "12px",
        bgcolor: "#f8fafc",
        border: "1px solid rgba(15,23,42,0.08)",
      }}
    >
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.6 }}>
        Class Summary
      </Typography>

      <Box sx={{ mt: 0.75 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <CalendarMonthOutlinedIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{dateLine}</Typography>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
          <AccessTimeOutlinedIcon sx={{ fontSize: 16, color: "#64748b" }} />
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#334155" }}>{timeLine}</Typography>
        </Stack>
      </Box>

      <Divider sx={{ my: 1.5 }} />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 }}>
        <Box>
          <SectionLabel>Teacher</SectionLabel>
          {pattern.staff ? (
            <>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.4 }}>
                <PersonOutlineOutlinedIcon sx={{ fontSize: 16, color: "#64748b" }} />
                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#1e293b" }}>
                  {pattern.staff.name}
                </Typography>
              </Stack>
              {pattern.location?.name && (
                <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 0.1, pl: 2.9 }}>
                  {pattern.location.name}
                </Typography>
              )}
            </>
          ) : (
            <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: 0.4 }}>Unassigned</Typography>
          )}
        </Box>

        <Box>
          <SectionLabel>Location</SectionLabel>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.4 }}>
            <PlaceOutlinedIcon sx={{ fontSize: 16, color: "#64748b" }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
              {pattern.location?.name || (pattern.study_mode === "online" ? "Online" : "—")}
            </Typography>
          </Stack>
        </Box>

        <Box>
          <SectionLabel>Enrollment</SectionLabel>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.4 }}>
            <GroupsOutlinedIcon sx={{ fontSize: 16, color: "#64748b" }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
              {pattern.capacity > 0 ? `${pattern.enrolled} / ${pattern.capacity}` : `${pattern.enrolled} enrolled`}
            </Typography>
          </Stack>
        </Box>

        {grades.length > 0 && (
          <Box>
            <SectionLabel>Grades</SectionLabel>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#334155", mt: 0.4 }}>
              {grades.join(", ")}
            </Typography>
          </Box>
        )}
      </Box>

      {uniqueSubjects.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <SectionLabel>Subjects</SectionLabel>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, mt: 0.6 }}>
            {subjectsToRender.map((s, i) => (
              <Chip
                key={`${s.id}-${i}`}
                label={s.name}
                size="small"
                sx={{
                  height: 22, fontSize: 11, fontWeight: 600,
                  bgcolor: "#fff", border: "1px solid rgba(15,23,42,0.1)",
                }}
              />
            ))}
            {moreCount > 0 && !expanded && (
              <Chip
                label={`+${moreCount} more`}
                size="small"
                onClick={() => setExpanded(true)}
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700, cursor: "pointer",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              />
            )}
            {expanded && uniqueSubjects.length > SUBJECT_CAP && (
              <Chip
                label="Show less"
                size="small"
                onClick={() => setExpanded(false)}
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700, cursor: "pointer",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

/**
 * Dedicated Class Dates row — always rendered between "Student to release"
 * and "Release scope", so the user can see the series start/end before
 * choosing a scope. Independent of the collapsible Class Summary card up
 * top (which may scroll off on small viewports). The null end-date case
 * renders an explicit "No end date" value rather than hiding the field.
 */
const ClassDatesRow: React.FC<{
  seriesStartDate: string | null;
  seriesEndDate: string | null;
}> = ({ seriesStartDate, seriesEndDate }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        px: 1.5, py: 1.25, borderRadius: "10px",
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
        display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5,
      }}
    >
      <Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>
          Class Start
        </Typography>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#1e293b", mt: 0.4 }}>
          {seriesStartDate ? dayjs(seriesStartDate).format("MMM D, YYYY") : "—"}
        </Typography>
      </Box>
      <Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5 }}>
          Class End
        </Typography>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#1e293b", mt: 0.4 }}>
          {seriesEndDate
            ? dayjs(seriesEndDate).format("MMM D, YYYY")
            : <span style={{ color: "#64748b", fontStyle: "italic", fontWeight: 500 }}>No end date</span>}
        </Typography>
      </Box>
    </Box>
  );
};

const StudentField: React.FC<{
  students: SchedulePattern["students"];
  value: SchedulePattern["students"][number] | null;
  onChange: (s: SchedulePattern["students"][number] | null) => void;
}> = ({ students, value, onChange }) => {
  const singlePreselected = students.length === 1 && value && value.id === students[0].id;

  if (singlePreselected && value) {
    return (
      <Box>
        <SectionLabel>Student to release</SectionLabel>
        <Stack
          direction="row" spacing={1.25} alignItems="center"
          sx={{
            mt: 0.6, px: 1.25, py: 1, borderRadius: "10px",
            bgcolor: "#fff", border: "1px solid rgba(15,23,42,0.1)",
          }}
        >
          <Avatar src={value.image || undefined} sx={{ width: 32, height: 32, fontSize: 12, fontWeight: 700 }}>
            {initials(value.name)}
          </Avatar>
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#1e293b" }}>{value.name}</Typography>
            {value.grade != null && value.grade !== "" && (
              <Typography sx={{ fontSize: 11.5, color: "#64748b" }}>Grade {value.grade}</Typography>
            )}
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <SectionLabel>Student to release</SectionLabel>
      <Autocomplete
        sx={{ mt: 0.6 }}
        options={students}
        getOptionLabel={(o) => o.name}
        value={value}
        onChange={(_, val) => onChange(val)}
        renderInput={(params) => <TextField {...params} size="small" placeholder="Select a student…" />}
        isOptionEqualToValue={(a, b) => a.id === b.id}
      />
    </Box>
  );
};

const ScopeOption: React.FC<{
  value: Scope;
  selected: boolean;
  disabled?: boolean;
  title: string;
  description: string;
}> = ({ value, selected, disabled, title, description }) => {
  const theme = useTheme();
  return (
    <FormControlLabel
      value={value}
      disabled={disabled}
      sx={{
        m: 0, mb: 0.75, alignItems: "flex-start",
        px: 1.25, py: 1, borderRadius: "10px",
        border: `1px solid ${selected && !disabled ? theme.palette.primary.main : "rgba(15,23,42,0.1)"}`,
        bgcolor: selected && !disabled ? alpha(theme.palette.primary.main, 0.04) : "transparent",
        opacity: disabled ? 0.55 : 1,
        transition: "border-color 120ms, background-color 120ms",
        "&:hover": disabled ? undefined : { borderColor: alpha(theme.palette.primary.main, 0.5) },
      }}
      control={<Radio size="small" sx={{ py: 0.25, mt: -0.25 }} />}
      label={
        <Box sx={{ ml: 0.25 }}>
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "#1e293b" }}>{title}</Typography>
          <Typography sx={{ fontSize: 11.5, color: "#64748b", mt: 0.25, lineHeight: 1.4 }}>{description}</Typography>
        </Box>
      }
    />
  );
};

const SafetyMessage: React.FC<{ scope: Scope }> = ({ scope }) => {
  const text = scope === "occurrence"
    ? "This removes the student from this session only. The recurring class and future sessions remain unchanged."
    : "The class itself is not cancelled. Other students remain enrolled. Past attendance and invoiced sessions are preserved.";
  return (
    <Box
      sx={{
        px: 1.25, py: 1, borderRadius: "10px",
        bgcolor: "rgba(234,179,8,0.08)",
        border: "1px solid rgba(234,179,8,0.25)",
        display: "flex", gap: 1, alignItems: "flex-start",
      }}
    >
      <InfoOutlinedIcon sx={{ fontSize: 16, color: "#a16207", mt: 0.1 }} />
      <Typography sx={{ fontSize: 11.75, color: "#854d0e", lineHeight: 1.5 }}>
        {text}
      </Typography>
    </Box>
  );
};

export default ReleaseStudentDialog;
